import { sanityClient, urlFor } from './client';
import type { SiteContent } from './types';

const QUERY = /* groq */ `{
  "site": *[_id == "siteSettings"][0]{
    title, description, contactEmail, contactPhone, footerCopyright, rawMetaTags, navItems
  },
  "hero": *[_id == "hero"][0]{
    backgroundImage, backgroundAlt, logoImage, logoAlt, tagline, ctaLabel
  },
  "about": *[_id == "about"][0]{
    heading, intro, body, closingPrefix, closingHighlight
  },
  "gallery": *[_id == "gallery"][0]{
    textOne, textTwo, textureImage, images
  },
  "capabilities": *[_id == "capabilities"][0]{
    heading, plaques
  },
  "selectWork": *[_id == "selectWork"][0]{
    eyebrow, heading, intro,
    projects[]{
      title, "id": slug.current, location, year, tags, descriptor,
      coverImage, coverImageUrl, coverImageAlt,
      heroImage, heroImageUrl, heroImageAlt,
      context, approach, outcome
    }
  },
  "contact": *[_id == "contactCopy"][0]{
    heading, body, contactEmail, contactPhone, ctaLabel, responseTime
  },
  "services": *[_type == "serviceSection" && defined(slug.current)] | order(order asc, _createdAt asc){
    "id": slug.current, order, reverse, title, intro, bulletHeading, bullets,
    backgroundImage, backgroundImageUrl, backgroundAlt, contactLabel
  }
}`;

interface RawResponse {
  site: {
    title?: string;
    description?: string;
    contactEmail?: string;
    contactPhone?: string;
    footerCopyright?: string;
    rawMetaTags?: string;
    navItems?: Array<{ label?: string; action?: string; sectionId?: string }>;
  } | null;
  hero: {
    backgroundImage?: unknown;
    backgroundAlt?: string;
    logoImage?: unknown;
    logoAlt?: string;
    tagline?: string;
    ctaLabel?: string;
  } | null;
  about: {
    heading?: string;
    intro?: string;
    body?: string;
    closingPrefix?: string;
    closingHighlight?: string;
  } | null;
  selectWork: {
    eyebrow?: string;
    heading?: string;
    intro?: string;
    projects?: Array<{
      title?: string;
      id?: string;
      location?: string;
      year?: string;
      tags?: string[];
      descriptor?: string;
      coverImage?: unknown;
      coverImageUrl?: string;
      coverImageAlt?: string;
      heroImage?: unknown;
      heroImageUrl?: string;
      heroImageAlt?: string;
      context?: string;
      approach?: string[];
      outcome?: string;
    }>;
  } | null;
  gallery: {
    textOne?: string;
    textTwo?: string;
    textureImage?: unknown;
    images?: Array<{ image?: unknown; alt?: string }>;
  } | null;
  capabilities: {
    heading?: string;
    plaques?: Array<{ title?: string; description?: string; linkedServiceId?: string; contactLabel?: string }>;
  } | null;
  contact: {
    heading?: string;
    body?: string;
    contactEmail?: string;
    contactPhone?: string;
    ctaLabel?: string;
    responseTime?: string;
  } | null;
  services: Array<{
    id?: string;
    order?: number;
    reverse?: boolean;
    title?: string;
    intro?: string;
    bulletHeading?: string;
    bullets?: string[];
    backgroundImage?: unknown;
    backgroundImageUrl?: string;
    backgroundAlt?: string;
    contactLabel?: string;
  }>;
}

export async function loadSiteContent(opts: { fresh?: boolean } = {}): Promise<SiteContent> {
  const data = await sanityClient.fetch<RawResponse>(
    QUERY,
    {},
    opts.fresh ? { useCdn: false } : {},
  );

  if (!data.hero) throw new Error('Hero document not found in Sanity.');

  return {
    site: {
      title: data.site?.title ?? 'ADL Solutions',
      description: data.site?.description ?? '',
      contactEmail: data.site?.contactEmail ?? '',
      contactPhone: data.site?.contactPhone ?? '',
      footerCopyright: data.site?.footerCopyright ?? '© {year} ADL Solutions. All rights reserved.',
      rawMetaTags: data.site?.rawMetaTags ?? '',
      nav: (data.site?.navItems ?? []).map((n) => ({
        label: n.label ?? '',
        action: (n.action === 'contact' ? 'contact' : 'scroll') as 'scroll' | 'contact',
        sectionId: n.sectionId,
      })),
    },
    hero: {
      backgroundImageUrl: urlFor(data.hero.backgroundImage as never),
      backgroundAlt: data.hero.backgroundAlt ?? '',
      logoImageUrl: urlFor(data.hero.logoImage as never),
      logoAlt: data.hero.logoAlt ?? 'ADL Solutions',
      tagline: data.hero.tagline ?? '',
      ctaLabel: data.hero.ctaLabel ?? 'get in touch',
    },
    about: {
      heading: data.about?.heading ?? '',
      intro: data.about?.intro ?? '',
      body: data.about?.body ?? '',
      closingPrefix: data.about?.closingPrefix ?? '',
      closingHighlight: data.about?.closingHighlight ?? '',
    },
    selectWork: {
      eyebrow: data.selectWork?.eyebrow ?? 'Selected Work',
      heading: data.selectWork?.heading ?? '',
      intro: data.selectWork?.intro ?? '',
      projects: (data.selectWork?.projects ?? []).map((p) => ({
        id: p.id ?? '',
        title: p.title ?? '',
        location: p.location ?? '',
        year: p.year ?? '',
        tags: p.tags ?? [],
        descriptor: p.descriptor ?? '',
        coverImageUrl:
          urlFor(p.coverImage as never) || p.coverImageUrl || '',
        coverImageAlt: p.coverImageAlt ?? '',
        heroImageUrl:
          urlFor(p.heroImage as never) || p.heroImageUrl || '',
        heroImageAlt: p.heroImageAlt ?? '',
        context: p.context ?? '',
        approach: p.approach ?? [],
        outcome: p.outcome ?? '',
      })),
    },
    gallery: {
      textOne: data.gallery?.textOne ?? '',
      textTwo: data.gallery?.textTwo ?? '',
      textureImageUrl: urlFor(data.gallery?.textureImage as never),
      images: (data.gallery?.images ?? []).map((i) => ({
        url: urlFor(i.image as never),
        alt: i.alt ?? '',
      })),
    },
    capabilities: {
      heading: data.capabilities?.heading ?? 'Core Capabilities',
      plaques: (data.capabilities?.plaques ?? []).map((p) => ({
        title: p.title ?? '',
        description: p.description ?? '',
        linkedServiceId: p.linkedServiceId ?? '',
        contactLabel: p.contactLabel ?? 'Contact now',
      })),
    },
    services: (data.services ?? []).map((s) => ({
      id: s.id ?? '',
      order: s.order ?? 0,
      reverse: Boolean(s.reverse),
      title: (s.title ?? '').trim(),
      intro: s.intro ?? '',
      bulletHeading: s.bulletHeading ?? 'Core scope',
      bullets: s.bullets ?? [],
      backgroundImageUrl:
        urlFor(s.backgroundImage as never) || s.backgroundImageUrl || '',
      backgroundAlt: s.backgroundAlt ?? '',
      contactLabel: s.contactLabel ?? 'Contact now',
    })),
    contact: {
      heading: data.contact?.heading ?? 'Get in touch',
      body: data.contact?.body ?? '',
      email: data.contact?.contactEmail ?? '',
      phone: data.contact?.contactPhone ?? '',
      ctaLabel: data.contact?.ctaLabel ?? 'Start the conversation',
      responseTime: data.contact?.responseTime ?? '',
    },
  };
}
